import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { STEPS } from '../store/loanApplicationSlice';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidSaveEmail(email) {
  return EMAIL_RE.test(String(email || '').trim());
}

/**
 * Persist conversational assessment progress for a guest (email only, no login).
 */
export async function saveGuestApplication({
  email,
  applicationId,
  productRecommendation,
  recommendedProduct,
  recommendedProducts,
  readinessScore,
  currentStep,
  v1,
  messages,
}) {
  if (!isSupabaseConfigured) {
    throw new Error('Saving is not available — Supabase is not configured.');
  }

  const guestEmail = String(email || '').trim().toLowerCase();
  if (!isValidSaveEmail(guestEmail)) {
    throw new Error('Please enter a valid email address.');
  }

  const assessmentAnswers = {
    version: 2,
    savedAt: new Date().toISOString(),
    contactEmail: guestEmail,
    productRecommendation: productRecommendation || {},
    recommendedProduct: recommendedProduct || null,
    recommendedProducts: recommendedProducts || [],
    v1: v1 || null,
    messages: (messages || []).map((m) => ({
      role: m.role,
      text: m.text,
      questionId: m.questionId || null,
      id: m.id || null,
    })),
  };

  const row = {
    guest_email: guestEmail,
    applicant_id: null,
    status: 'draft',
    current_step: currentStep ?? STEPS.ASSESSMENT,
    assessment_answers: assessmentAnswers,
    readiness_score: readinessScore ?? 0,
    updated_at: new Date().toISOString(),
  };

  if (applicationId) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update(row)
      .eq('id', applicationId)
      .eq('guest_email', guestEmail)
      .select('id, guest_email, updated_at')
      .maybeSingle();

    if (error) throw error;
    if (data) return data;
  }

  const { data: existing, error: lookupError } = await supabase
    .from('loan_applications')
    .select('id')
    .eq('guest_email', guestEmail)
    .is('applicant_id', null)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (lookupError) throw lookupError;

  if (existing?.id) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update(row)
      .eq('id', existing.id)
      .select('id, guest_email, updated_at')
      .single();

    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from('loan_applications')
    .insert(row)
    .select('id, guest_email, updated_at')
    .single();

  if (error) throw error;
  return data;
}
